import { IonButton, IonButtons, IonContent, IonIcon, IonImg, IonPage, useIonViewDidLeave, useIonViewWillEnter } from "@ionic/react";
import { arrowBackOutline } from "ionicons/icons";
import { useLayoutEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useTheme } from "react-jss";
import { RouteComponentProps } from "react-router";
import calendarImage from "../../../assets/image/calendar.png";
import truckImage from "../../../assets/image/simplyTruck.png";
import Header from "../../../components/header/header";
import { LabelComponent } from "../../../components/label/label";
import RefreshComponent from "../../../components/refresh/refreshComponent";
import { AppRoutes } from "../../../constants/appRoutes";
import { DateFormats } from "../../../constants/dateFormats";
import { DefaultValues } from "../../../constants/defaultValues";
import { RequestStates } from "../../../constants/requestStates";
import { fetchRequestItem, fetchRequestList } from "../../../redux/actions/request/request";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { setGlobalLoading } from "../../../redux/reducers/globalLoading/globalLoading";
import { resetDetailsRequestForm } from "../../../redux/reducers/request/detail/form";
import { LocalStorageKey, LocalStorageService } from "../../../services/localStorageService";
import { convertToLocal } from "../../../utils/dateUtils";
import { AboutSecurityController } from "../../aboutSecurity/controller";
import { RequestDetailContainerStyle } from "./containerStyle.jss";
import { RequestDetailProgressBar } from "./progressBar/progressBar";

interface IRequestDetails
    extends RouteComponentProps<{
        id: string;
    }> {}

export const RequestDetailContainer: React.FC<IRequestDetails> = ({ match, history }) => {
    const theme = useTheme();
    const style = RequestDetailContainerStyle({ theme });
    const dispatch = useAppDispatch();

    const { t } = useTranslation("common");
    const { form, loading, isLoaded } = useAppSelector((state) => state.detailsRequestForm);
    const { isClaimsLoaded } = useAppSelector((state) => state.userClaims);
    const localStorage = new LocalStorageService();
    const item = JSON.parse(localStorage.getItem(LocalStorageKey.USER_SIGNUP));
    const [isLoadedView, setIsLoadedView] = useState<boolean>(false);

    useIonViewDidLeave(() => {
        dispatch(resetDetailsRequestForm());
        dispatch(fetchRequestList());
        setIsLoadedView(false);
    }, [dispatch]);

    useIonViewWillEnter(() => {
        if (!isLoaded && !isLoadedView) {
            dispatch(setGlobalLoading(true));
            setIsLoadedView(true);
            dispatch(fetchRequestItem(match.params.id)).then((response: any) => {
                !response && history.replace(AppRoutes.REQUEST);
                dispatch(setGlobalLoading(false));
            });
        }
    }, [dispatch, isLoaded, isLoadedView]);

    useLayoutEffect(() => {
        if (!isClaimsLoaded && !item) {
            return history.push(AppRoutes.REQUEST);
        }
    }, [item]);

    const RequestStateDescriptionCard = () => {
        switch (form.stateSort) {
            case RequestStates.PREPARED:
                return "PAGE.REQUEST.DESCRIPTION_STATE.PREPARED";
            case RequestStates.PRESENTED:
                return "PAGE.REQUEST.DESCRIPTION_STATE.PREPARED";
            case RequestStates.PREPARED_DOCK:
                return "PAGE.REQUEST.DESCRIPTION_STATE.PREPARED";
            case RequestStates.DOCK:
                return "PAGE.REQUEST.DESCRIPTION_STATE.DOCK";
            case RequestStates.DOCK_END:
                return "PAGE.REQUEST.DESCRIPTION_STATE.DOCK";
            default:
                return "PAGE.REQUEST.DESCRIPTION_STATE.EXIT_DOCK";
        }
    };

    const RequestStateValuesCard = () => {
        switch (form.stateSort) {
            case RequestStates.PREPARED:
                return { transportUnitNumber: form.transportUnitNumber };
            case RequestStates.PRESENTED:
                return { transportUnitNumber: form.transportUnitNumber };
            case RequestStates.PREPARED_DOCK:
                return { transportUnitNumber: form.transportUnitNumber, dock: form.dockDescription };
            case RequestStates.DOCK:
                return { transportUnitNumber: form.transportUnitNumber };
            case RequestStates.DOCK_END:
                return { transportUnitNumber: form.transportUnitNumber };
            default:
                return { transportUnitNumber: form.transportUnitNumber };
        }
    };
    return (
        <IonPage>
            <Header
                element={
                    <IonButtons slot="start">
                        <IonButton
                            routerDirection="back"
                            routerLink={AppRoutes.REQUEST}
                            onClick={() => dispatch(resetDetailsRequestForm())}
                        >
                            <IonIcon
                                slot="icon-only"
                                icon={arrowBackOutline}
                                size={"large"}
                                className={style.icon}
                            />
                        </IonButton>
                    </IonButtons>
                }
                title={t("GLOBAL.DETAILS")}
                showLogout={false}
            />

            {isLoaded && (
                <IonContent>
                    <div className={style.container}>
                        <RefreshComponent
                            onRefreshComponent={() => {
                                dispatch(fetchRequestItem(form.id));
                            }}
                        />

                        <div className={style.header}>
                            <div className={style.headerSection}>
                                <IonImg
                                    className={style.image}
                                    src={truckImage}
                                />
                                <span className={style.sectionTitle}>{form.transportUnitNumber}</span>
                            </div>

                            <div className={style.headerSection}>
                                <IonImg
                                    className={style.calendarImage}
                                    src={calendarImage}
                                />
                                <span className={style.date}>
                                    {form.carrierRegisterOn
                                        ? convertToLocal(form.carrierRegisterOn, DateFormats.DATE_HOUR_MINUTE)
                                        : `${DefaultValues.DEFAULT_DATE} ${DefaultValues.DEFAULT_TIME}`}
                                </span>
                            </div>
                        </div>

                        <div className={style.stateDescription}>
                            {loading ? (
                                DefaultValues.NOT_DATA
                            ) : (
                                <Trans
                                    i18nKey={RequestStateDescriptionCard()}
                                    values={RequestStateValuesCard()}
                                />
                            )}
                        </div>

                        <div className={style.dataContent}>
                            <div className={style.sectionDataContent}>
                                <LabelComponent
                                    title={t("PAGE.DETAILS_REQUEST.DOCK")}
                                    value={form.dockDescription ?? DefaultValues.NOT_DATA}
                                    strong
                                />
                            </div>
                            {form.originSeals && (
                                <div className={style.sectionDataContent}>
                                    <LabelComponent
                                        title={t("PAGE.DETAILS_REQUEST.ORIGIN_SEAL")}
                                        value={form.originSeals.length > 0 ? form.originSeals.join(", ") : DefaultValues.NOT_DATA}
                                        strong
                                    />
                                </div>
                            )}
                            {form.newSeals && (
                                <div className={style.sectionDataContent}>
                                    <LabelComponent
                                        title={t("PAGE.DETAILS_REQUEST.NEW_SEAL")}
                                        value={form.newSeals.length > 0 ? form.newSeals.join(", ") : DefaultValues.NOT_DATA}
                                        strong
                                    />
                                </div>
                            )}
                        </div>

                        <AboutSecurityController fromRequest />

                        <RequestDetailProgressBar
                            visible={isLoaded}
                            request={form}
                        />
                    </div>
                </IonContent>
            )}
        </IonPage>
    );
};
